/**
 * Progressive enhancement for snippets/facets.liquid, snippets/collection-type-nav.liquid and
 * snippets/pagination.liquid: every control here is a real link, checkbox, radio, number input or
 * submit button first, so the collection and search pages filter, sort and paginate with no JS at
 * all (a plain GET reload). `<collection-facets>` (wrapping the type row, the filter bar/sheet, the
 * grid and pagination in sections/main-collection.liquid and sections/main-search.liquid) turns those
 * same actions into an in-place fetch of the `{% partial %}` regions inside it instead:
 *
 *  - Any change inside a `[data-facets-form]` or `[data-facets-sheet-form]` (checkboxes, the price
 *    inputs, the sort selects) re-renders after a short debounce.
 *  - Submitting a form (the desktop bar's Apply, the mobile sort popover, the sheet's
 *    "Show N Results") re-renders immediately.
 *  - Clicking a plain `a[data-facets-url]` (an active filter chip, "Clear All", a type row pill)
 *    re-renders immediately with that link's own query. Numbered pagination links are plain
 *    navigations, so a new page starts at the top.
 *  - Clicking a "Load More" link (`[data-pagination-more]`) fetches the next page and appends its
 *    cards to the current grid instead of replacing it, without touching the URL.
 *
 * Every render swaps only the partials, preserves which popover or
 * `<details>` group was open, keeps focus and the sheet's scroll position (the open filter sheet
 * itself stays in place and only its content is replaced), updates the URL with
 * `history.pushState` (except for "Load More"), and announces the new result count in a live
 * region. `popstate` re-renders from the URL without pushing a new entry. Each render runs inside
 * the standard event `shopify:collection:update` or `shopify:search:update`.
 */

const DEBOUNCE_MS = 300;

// Firefox refuses an import map once a module has started to load, and Shopify's own loaders in
// `content_for_header` start before the map it prints there, so the address is read from the map.
function partialRuntimeUrl() {
  const specifier = "@shopify/partial-rendering";
  for (const map of document.querySelectorAll('script[type="importmap"]')) {
    const url = JSON.parse(map.textContent).imports?.[specifier];
    if (url) return url;
  }
  return specifier;
}

function withoutInternalParams(params) {
  params.delete("section_id");
  return params;
}

class CollectionFacets extends HTMLElement {
  #abort = null;
  #debounce = 0;
  #pricePending = null;
  #focusResults = false;

  connectedCallback() {
    this.addEventListener("focusout", this.#onFocusOut);
    this.addEventListener("change", this.#onChange);
    this.addEventListener("submit", this.#onSubmit);
    this.addEventListener("click", this.#onClick);
    // `toggle` does not bubble: caught on its way down.
    this.addEventListener("toggle", this.#onToggle, true);
    window.addEventListener("popstate", this.#onPopState);

    // The current type's tab, centered in its row. The row alone is scrolled: scrollIntoView
    // moved the page too where the row stood below the window's edge (a phone held sideways).
    const tab = this.querySelector("[data-facets-typenav] [aria-current]");
    const row = tab?.closest(".scroller");
    if (row) {
      const offset = tab.getBoundingClientRect().left - row.getBoundingClientRect().left;
      row.scrollLeft += offset - (row.clientWidth - tab.offsetWidth) / 2;
    }
  }

  disconnectedCallback() {
    this.removeEventListener("focusout", this.#onFocusOut);
    this.removeEventListener("change", this.#onChange);
    this.removeEventListener("submit", this.#onSubmit);
    this.removeEventListener("click", this.#onClick);
    this.removeEventListener("toggle", this.#onToggle, true);
    window.removeEventListener("popstate", this.#onPopState);
    clearTimeout(this.#debounce);
    this.#abort?.abort();
  }

  // A dropdown is never higher than the room it opens into. The stylesheet says so with a
  // percentage of that room (`.facets__panel` in snippets/facets.liquid), and Safari 26 takes
  // the percentage of the whole window instead: on iOS 26.5 the sort panel ran 134 px out of the
  // screen's lower edge and its last options could not be reached (2026-10-05; Safari 27,
  // Chromium and Firefox keep the panel inside). A panel that still stands out of the window once
  // it is open is capped here by measurement, and its list scrolls. The page behind an open
  // panel does not scroll, so the measure holds until the panel closes.
  #onToggle = (event) => {
    const panel = event.target;
    if (!(panel instanceof HTMLElement) || !panel.classList.contains("facets__panel")) return;
    panel.style.removeProperty("max-block-size");
    if (event.newState !== "open") return;
    const box = panel.getBoundingClientRect();
    const gap = parseFloat(getComputedStyle(panel).marginBlockEnd) || 0;
    const excess = Math.max(box.bottom + gap - window.innerHeight, gap - box.top);
    if (excess > 1) panel.style.maxBlockSize = `${Math.max(0, box.height - excess)}px`;
  };

  #onChange = (event) => {
    const form = event.target.closest("[data-facets-form], [data-facets-sheet-form]");
    if (!form) return;
    // A price is a pair of fields: a change of one waits until the focus has left the pair
    // (#onFocusOut). Rendering in between replaced the field the shopper had moved on to, and
    // what they had typed into it by then was gone.
    if (event.target.closest(".facets-price")) {
      this.#pricePending = form;
      return;
    }
    clearTimeout(this.#debounce);
    this.#debounce = setTimeout(() => this.#renderForm(form), DEBOUNCE_MS);
  };

  #onSubmit = (event) => {
    const form = event.target.closest("[data-facets-form], [data-facets-sheet-form]");
    if (!form) return;
    event.preventDefault();
    this.#pricePending = null;
    clearTimeout(this.#debounce);
    // "Show N Results" shows them: the sheet closes. (It stayed open before, and only its sliding
    // in again told the shopper that anything had happened.)
    if (form.matches("[data-facets-sheet-form]")) {
      form.closest("[popover]")?.hidePopover?.();
      // The focus was in the sheet: it goes on to the results once they are in (#restoreState).
      this.#focusResults = true;
    }
    this.#renderForm(form);
  };

  // The focus leaves a price pair that was changed: now it renders.
  #onFocusOut = (event) => {
    const form = this.#pricePending;
    const pair = event.target instanceof Element ? event.target.closest(".facets-price") : null;
    if (!form || !pair || pair.contains(event.relatedTarget)) return;
    clearTimeout(this.#debounce);
    this.#debounce = setTimeout(() => {
      if (this.#pricePending !== form) return;
      // Back in a price field by now (a click on the other field's label blurs first and focuses
      // after): the change stays pending until the focus leaves the pair for good.
      const active = document.activeElement;
      if (form.contains(active) && active.closest(".facets-price")) return;
      this.#pricePending = null;
      if (form.isConnected) this.#renderForm(form);
    }, DEBOUNCE_MS);
  };

  #onClick = (event) => {
    const moreLink = event.target.closest("[data-pagination-more]");
    // On a page after the first (reached from an external link) the earlier cards aren't on screen,
    // so appending would mislabel the running total; that "Load More" stays a plain next-page link.
    if (moreLink && !this.querySelector("[data-pagination-previous]")) {
      event.preventDefault();
      // Already loading: a second click must not abort and restart the same request.
      if (moreLink.getAttribute("aria-busy") === "true") return;
      this.#loadMore(moreLink);
      return;
    }

    const link = event.target.closest("a[data-facets-url]");
    if (!link) return;
    const url = new URL(link.href, window.location.origin);
    // Only links that stay on this collection or search re-render in place (the fetch always uses
    // the current path). A link to another page is a plain navigation.
    if (url.pathname !== window.location.pathname) return;
    event.preventDefault();
    // The link says what is wanted now: a change that was still waiting (a price) must not render
    // over it.
    clearTimeout(this.#debounce);
    this.#pricePending = null;
    this.#render(withoutInternalParams(url.searchParams));
  };

  #onPopState = () => {
    if (!this.isConnected) return;
    this.#render(withoutInternalParams(new URLSearchParams(window.location.search)), { updateHistory: false });
  };

  #renderForm(form) {
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form)) {
      if (value !== "") params.append(key, value);
    }
    this.#render(withoutInternalParams(params));
  }

  #captureState() {
    const sheet = this.querySelector("[data-facets-sheet]");
    const active = this.contains(document.activeElement) ? document.activeElement : null;
    // A bar panel can have the focus itself (a click on its padding; in Safari a tap on one of its
    // values, which gives the checkbox no focus). It is found again by its filter, not by its id:
    // the ids count the filters, so after a filter came or went the id can be another panel's, a
    // closed one that takes no focus and would keep the fallback from running.
    const activePanelKey = active?.matches(".facets-bar [popover][data-facets-key]") ? active.dataset.facetsKey : null;
    return {
      sheetOpen: sheet?.matches(":popover-open") ?? false,
      sheetScroll: sheet?.querySelector(".facets-sheet__body")?.scrollTop ?? 0,
      groupKeys: [...this.querySelectorAll("[data-facets-sheet] details[data-facets-key]")].map(
        (details) => details.dataset.facetsKey
      ),
      openGroupKeys: [...this.querySelectorAll("[data-facets-sheet] details[data-facets-key][open]")].map(
        (details) => details.dataset.facetsKey
      ),
      openPanelKey: [...this.querySelectorAll(".facets-bar [popover][data-facets-key]")].find((panel) =>
        panel.matches(":popover-open")
      )?.dataset.facetsKey,
      activePanelKey,
      activeId: active && !activePanelKey ? active.id : null,
      focusWasInside: Boolean(active),
    };
  }

  #restoreState(state) {
    if (state.openPanelKey) {
      this.querySelector(`.facets-bar [popover][data-facets-key="${state.openPanelKey}"]`)?.showPopover?.();
    }
    // Every group the shopper has seen keeps the state they left it in, a closed first group too
    // (the fresh render opens the first). A group that is new keeps the render's state.
    for (const group of this.querySelectorAll("[data-facets-sheet] details[data-facets-key]")) {
      const key = group.dataset.facetsKey;
      if (state.groupKeys.includes(key)) group.open = state.openGroupKeys.includes(key);
    }
    if (state.sheetOpen) {
      const body = this.querySelector("[data-facets-sheet] .facets-sheet__body");
      if (body) body.scrollTop = state.sheetScroll;
    }

    if (this.#focusResults) {
      this.#focusResults = false;
      state = { ...state, activeId: null, activePanelKey: null, focusWasInside: true, sheetOpen: false };
    }

    const panel = state.activePanelKey
      ? this.querySelector(`.facets-bar [popover][data-facets-key="${state.activePanelKey}"]`)
      : null;
    if (panel?.matches(":popover-open")) {
      panel.focus({ preventScroll: true });
    } else if (state.activeId && document.getElementById(state.activeId)) {
      document.getElementById(state.activeId).focus({ preventScroll: true });
    } else if (state.focusWasInside) {
      // The focused control is gone (a removed filter pill): the sheet's heading while the sheet
      // stays open, otherwise the first of the result count and the grid that is rendered (the
      // count sits in the desktop bar, hidden on phones).
      const candidates = state.sheetOpen
        ? [this.querySelector("[data-facets-sheet-heading]")]
        : [this.querySelector("[data-facets-count]"), this.querySelector("[data-facets-grid]")];
      const fallback = candidates.find((element) => element?.checkVisibility());
      if (fallback) {
        if (!fallback.hasAttribute("tabindex")) fallback.setAttribute("tabindex", "-1");
        fallback.focus({ preventScroll: true });
      }
    }
  }

  #announce(doc, override) {
    const live = this.querySelector("[data-facets-live]");
    const text = override || doc.querySelector("[data-facets-grid]")?.dataset.resultsText;
    if (!live || !text) return;
    live.textContent = "";
    requestAnimationFrame(() => {
      live.textContent = text;
    });
  }

  // A render inside the filters' standard event: the collection's, or the search's on the search page.
  #render(params, options) {
    const form = this.querySelector("[data-facets-form]") ?? this;
    const count = () => Number(this.querySelector("[data-facets-grid]")?.dataset.resultsCount ?? 0);
    const operation = () => this.#update(params, options);
    const { CollectionUpdateEvent, SearchUpdateEvent } = window.StandardEvents ?? {};
    if (this.dataset.searchTerms !== undefined) {
      const search = {
        query: this.dataset.searchTerms,
        productFilters: SearchUpdateEvent?.parseProductFilters(params),
        sortKey: SearchUpdateEvent?.getSortKey(params),
      };
      return window.trackStandardEvent(form, "SearchUpdateEvent", { search }, operation, () => ({ totalCount: count() }));
    }
    const { collectionId, collectionHandle, collectionCount } = this.dataset;
    const payload = {
      collection: { id: collectionId, handle: collectionHandle, productsCount: Number(collectionCount) },
      productFilters: CollectionUpdateEvent?.parseProductFilters(params),
      sortKey: CollectionUpdateEvent?.getSortKey(params),
    };
    return window.trackStandardEvent(form, "CollectionUpdateEvent", payload, operation, () => ({ productsCount: count() }));
  }

  async #fetchPartials(params) {
    const controller = new AbortController();
    this.#abort = controller;
    const url = new URL(window.location.pathname, window.location.origin);
    url.search = params.toString();
    // Imported here, not at the top: without the runtime the filters still work as plain GET forms.
    const { partials } = await import(partialRuntimeUrl());
    const names = partials.getAll(this).map(({ name }) => name);
    return { partials, update: await partials.fetch(...names, { url: url.toString(), signal: controller.signal }), url };
  }

  async #update(params, { updateHistory = true } = {}) {
    this.#abort?.abort();
    this.setAttribute("aria-busy", "true");

    let result;
    try {
      result = await this.#fetchPartials(params);
    } catch (error) {
      if (error.name === "AbortError") return;
      window.location.search = params.toString();
      return;
    } finally {
      this.removeAttribute("aria-busy");
    }

    const { partials, update, url } = result;
    // Not the partials we asked for (an error page, a redirect): load the page for real.
    if (!update.html.includes("<template for=")) {
      window.location.search = params.toString();
      return;
    }
    // What is open, scrolled and focused at this moment, not when the request left: the shopper may
    // have closed the sheet or opened another group while it was under way.
    const state = this.#captureState();
    partials.apply(update);
    // `apply()` puts the old checkboxes' state back on the fresh ones. The server's state is the
    // right one (a removed filter's box is unticked), and it stands in the fresh markup.
    for (const form of this.querySelectorAll("form")) form.reset();
    this.#announce(this);
    this.#restoreState(state);
    if (updateHistory) window.history.pushState({}, "", url);
  }

  async #loadMore(link) {
    const href = link.href;
    this.#abort?.abort();
    const controller = new AbortController();
    this.#abort = controller;
    this.setAttribute("aria-busy", "true");
    // Swaps the label for the button's spinner right away (theme.css, .button[aria-busy]).
    link.setAttribute("aria-busy", "true");

    const grid = this.querySelector("[data-facets-grid]");
    const pagination = this.querySelector("[data-facets-pagination]");

    let doc;
    try {
      const fetchUrl = new URL(href, window.location.origin);
      fetchUrl.searchParams.set("section_id", this.dataset.sectionId);
      const response = await fetch(fetchUrl, { signal: controller.signal });
      if (!response.ok) throw new Error("facets_fetch_failed");
      doc = new DOMParser().parseFromString(await response.text(), "text/html");
    } catch (error) {
      if (error.name === "AbortError") {
        link.removeAttribute("aria-busy");
        return;
      }
      // Falls back to opening the next page; the spinner stays until the browser navigates.
      window.location.href = href;
      return;
    } finally {
      this.removeAttribute("aria-busy");
    }

    const nextGrid = doc.querySelector("[data-facets-grid]");
    const nextPagination = doc.querySelector("[data-facets-pagination]");
    const firstNew = nextGrid?.firstElementChild;
    if (grid && nextGrid) {
      grid.append(...nextGrid.childNodes);
      grid.dataset.resultsText = nextGrid.dataset.resultsText || grid.dataset.resultsText;
    }
    let total = "";
    if (pagination && nextPagination) {
      // Every earlier card is on screen now: show the running total, not that page's own range.
      nextPagination.querySelectorAll("[data-pagination-range], [data-pagination-previous]").forEach((el) => el.remove());
      const totalLine = nextPagination.querySelector("[data-pagination-total]");
      totalLine?.removeAttribute("hidden");
      total = totalLine?.textContent.trim() ?? "";
      pagination.replaceWith(nextPagination);
    } else if (pagination) {
      pagination.remove();
    }

    // The activated link is gone with the old pagination: continue at the first new card, whose
    // title link is its primary link, instead of dropping focus to <body>.
    (firstNew?.querySelector(".link") ?? firstNew?.querySelector("a[href]"))?.focus();

    // The URL stays on the first page: a pushed ?page=N would make Back remove the appended cards
    // and a reload show that page alone. The live region reads the running total ("Showing 48 of
    // 53"), not the fetched page's own count.
    this.#announce(doc, total);
  }
}

customElements.define("collection-facets", CollectionFacets);
